import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Loader2, Check, X, Pencil, Sparkles, ChevronDown, ShieldCheck, Send, Clock, Rocket, Users, UserPlus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";

const Card = ({ children, className = "", ...props }) => <div {...props} className={`bg-white rounded-3xl border border-black/5 p-6 ${className}`}>{children}</div>;
const Mock = () => <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">MOCK</span>;
const H = ({ children }) => <h2 className="font-display font-extrabold text-2xl text-[#1A2421] mb-1">{children}</h2>;

export function DailyBrief() {
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/dashboard/brief").then((r) => setBrief(r.data)).catch((e) => console.error("Failed to load daily brief", e)), []);
  useEffect(() => { load(); }, [load]);
  const regen = async () => { setBusy(true); try { const r = await api.post("/dashboard/brief/regenerate"); setBrief(r.data); } catch (e) { console.error("Failed to regenerate brief", e); } setBusy(false); };
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><H>Daily Brief</H><p className="text-sm text-[#4A5550]">From your Chief of Staff</p></div>
        <button data-testid="regen-brief" onClick={regen} disabled={busy} className="inline-flex items-center gap-2 bg-[#224733] text-white font-bold text-sm px-5 py-2.5 rounded-full disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Regenerate brief
        </button>
      </div>
      <Card><div data-testid="brief-body" className="prose-sm whitespace-pre-wrap text-[#1A2421] leading-relaxed">{brief ? brief.body : "Loading…"}</div></Card>
    </div>
  );
}

export function Research() {
  const [items, setItems] = useState([]);
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/research").then((r) => setItems(r.data)).catch((e) => console.error("Failed to load research", e)), []);
  useEffect(() => { load(); }, [load]);
  const req = async () => { if (!topic.trim()) return; setBusy(true); try { await api.post("/research", { topic }); setTopic(""); await load(); } catch (e) { console.error("Failed to request research", e); } setBusy(false); };
  return (
    <div className="space-y-5">
      <H>Research Analyst</H>
      <Card>
        <label className="text-xs font-bold uppercase tracking-wide text-[#4A5550] mb-1.5 block">Request research</label>
        <div className="flex gap-2">
          <input data-testid="research-input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Perth strata maintenance rules"
            className="flex-1 rounded-xl border border-black/10 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B848]" />
          <button data-testid="research-submit" onClick={req} disabled={busy} className="inline-flex items-center gap-2 bg-[#62B848] text-white font-bold text-sm px-5 py-2.5 rounded-full disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : "Research"}</button>
        </div>
      </Card>
      <div className="space-y-3">
        {items.map((b) => (
          <Card key={b.id} className="!p-0 overflow-hidden">
            <button onClick={() => setOpen(open === b.id ? null : b.id)} className="w-full text-left p-6 flex items-start justify-between gap-4">
              <div><div className="font-display font-bold text-[#1A2421]">{b.title}</div><div className="text-sm text-[#4A5550] mt-1">{b.takeaway}</div><div className="text-xs text-[#9aa39e] mt-1">{new Date(b.created_at).toLocaleDateString()}</div></div>
              <ChevronDown size={20} className={`shrink-0 transition-transform ${open === b.id ? "rotate-180" : ""}`} />
            </button>
            {open === b.id && <div className="px-6 pb-6 whitespace-pre-wrap text-sm text-[#1A2421] leading-relaxed border-t border-black/5 pt-4">{b.body}</div>}
          </Card>
        ))}
        {items.length === 0 && <Card><p className="text-[#4A5550] text-sm">No briefs yet — request one above.</p></Card>}
      </div>
    </div>
  );
}

export function Social() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/social").then((r) => setData(r.data)).catch((e) => console.error("Failed to load social", e)), []);
  useEffect(() => { load(); }, [load]);
  const gen = async () => { setBusy(true); try { await api.post("/social/generate"); await load(); } catch (e) { console.error("Failed to generate posts", e); } setBusy(false); };
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between"><H>Social &amp; Reputation</H>
        <button data-testid="social-generate" onClick={gen} disabled={busy} className="inline-flex items-center gap-2 bg-[#224733] text-white font-bold text-sm px-5 py-2.5 rounded-full disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Draft next week's posts</button>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <Card>
          <h3 className="font-display font-bold text-lg text-[#1A2421] mb-3 flex items-center gap-2">Weekly calendar <Mock /></h3>
          <div className="space-y-2">{(data?.calendar || []).map((c) => (
            <div key={`${c.day}-${c.channel}-${c.idea}`} className="flex items-center gap-3 text-sm border border-black/5 rounded-xl px-3 py-2"><span className="font-bold w-10 text-[#224733]">{c.day}</span><span className="text-[#62B848] font-semibold w-20">{c.channel}</span><span className="text-[#4A5550]">{c.idea}</span></div>
          ))}</div>
        </Card>
        <Card>
          <h3 className="font-display font-bold text-lg text-[#1A2421] mb-3">Google reviews</h3>
          <div data-testid="reviews-empty" className="text-center py-8 text-[#4A5550] text-sm bg-[#F8F9FA] rounded-2xl">
            <p className="font-semibold text-[#1A2421]">Awaiting Google API key</p>
            <p>Live reviews appear here once the Google Places key is connected. No fake data shown.</p>
          </div>
        </Card>
      </div>
      <div>
        <h3 className="font-display font-bold text-lg text-[#1A2421] mb-3">Drafted posts awaiting approval</h3>
        <div className="space-y-3">
          {(data?.drafts || []).map((d) => <Card key={d.id}><div className="text-xs font-bold text-[#62B848] mb-2">DRAFT · {new Date(d.created_at).toLocaleDateString()}</div><div className="whitespace-pre-wrap text-sm text-[#1A2421]">{d.body}</div></Card>)}
          {(!data?.drafts || data.drafts.length === 0) && <Card><p className="text-[#4A5550] text-sm">No drafts yet — generate some above or ask @social in Command Chat.</p></Card>}
        </div>
      </div>
    </div>
  );
}

export function Finance() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/finance").then((r) => setD(r.data)).catch((e) => console.error("Failed to load finance", e)); }, []);
  if (!d) return <Card><Loader2 className="animate-spin mx-auto text-[#62B848]" /></Card>;
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3"><H>Financial Markets</H><Mock /></div>
      <div className="grid md:grid-cols-3 gap-5">
        <Card><div className="text-xs uppercase font-bold text-[#4A5550]">Cash position</div><div className="font-display font-black text-4xl text-[#224733] mt-2">${d.cash_position.toLocaleString()}</div></Card>
        <Card><div className="text-xs uppercase font-bold text-[#4A5550]">Outstanding</div><div className="font-display font-black text-4xl text-[#1A2421] mt-2">${d.invoices_outstanding.reduce((a, x) => a + x.amount, 0).toLocaleString()}</div></Card>
        <Card><div className="text-xs uppercase font-bold text-[#4A5550]">Overdue invoices</div><div className="font-display font-black text-4xl text-[#1A2421] mt-2">{d.invoices_outstanding.length}</div></Card>
      </div>
      <Card>
        <h3 className="font-display font-bold text-lg text-[#1A2421] mb-3">Chase list (oldest first)</h3>
        <div className="space-y-2">{d.invoices_outstanding.map((iv) => (
          <div key={iv.invoice} className="flex items-center justify-between text-sm border border-black/5 rounded-xl px-4 py-2.5">
            <div><span className="font-bold text-[#1A2421]">{iv.client}</span> <span className="text-[#9aa39e]">· {iv.invoice}</span></div>
            <div className="flex items-center gap-4"><span className="font-semibold">${iv.amount.toLocaleString()}</span><span className={`text-xs font-bold px-2 py-1 rounded-full ${iv.days_overdue > 30 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{iv.days_overdue}d overdue</span></div>
          </div>
        ))}</div>
      </Card>
      <div className="grid md:grid-cols-2 gap-5">
        <Card><h3 className="font-display font-bold text-lg mb-3">Upcoming dates</h3>{d.upcoming_dates.map((u) => <div key={u.label} className="flex justify-between text-sm py-1.5 border-b border-black/5 last:border-0"><span>{u.label}</span><span className="font-semibold">{u.date}</span></div>)}</Card>
        <Card><h3 className="font-display font-bold text-lg mb-3">Materials watch</h3>{d.materials_watch.map((m) => <div key={m.item} className="flex justify-between text-sm py-1.5 border-b border-black/5 last:border-0"><span>{m.item}</span><span className="font-bold text-[#62B848]">{m.change}</span></div>)}</Card>
      </div>
    </div>
  );
}

const CHANNEL_LABEL = {
  email: "Email", sms: "SMS", social: "Social post", review_response: "Google review reply",
  website: "Website change", internal: "Internal",
};

export function Approvals({ focusId, onFocusHandled }) {
  const [items, setItems] = useState([]);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const load = useCallback(() => {
    api.get("/approvals").then((r) => setItems(r.data)).catch((e) => console.error("Failed to load approvals", e));
    api.get("/approvals/audit").then((r) => setLog(r.data)).catch((e) => console.error("Failed to load approvals audit", e));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!focusId || items.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`approval-card-${focusId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlight(focusId);
        setTimeout(() => setHighlight(null), 3000);
      }
      onFocusHandled?.();
    }, 250);
    return () => clearTimeout(t);
  }, [focusId, items]); // eslint-disable-line
  const act = async (id, payload) => { await api.post(`/approvals/${id}`, payload); await load(); };
  const approve = (id) => act(id, { action: "approve" });
  const edit = (a) => { const b = prompt("Edit before sending:", a.body); if (b != null) act(a.id, { action: "edit", body: b }); };
  const reject = (id) => { const reason = prompt("Reason for rejecting (optional — the agent learns from it):", ""); act(id, { action: "reject", reason: reason || "" }); };
  const pending = items.filter((i) => i.status === "pending");

  return (
    <div className="space-y-5">
      <H>Approval Queue</H>
      <div className="flex items-center gap-2 text-sm text-[#224733] bg-[#62B848]/10 border border-[#62B848]/25 rounded-xl px-4 py-3">
        <ShieldCheck size={18} className="text-[#62B848] shrink-0" />
        <span><b>Nothing leaves the business without you.</b> Every client-facing email, SMS, social post and review reply is drafted here and only sends when you approve it. {pending.length} awaiting you.</span>
      </div>
      <div className="space-y-3">
        {items.map((a) => (
          <Card key={a.id} id={`approval-card-${a.id}`} data-testid={`approval-${a.id}`} className={`transition-all ${a.status !== "pending" ? "opacity-70" : ""} ${highlight === a.id ? "ring-2 ring-[#62B848] ring-offset-2" : ""}`}>
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#224733]/8 text-[#224733]">{CHANNEL_LABEL[a.channel] || (a.type || "").replace("_", " ")}</span>
                  <span className="font-display font-bold text-[#1A2421]">{a.title}</span>
                </div>
                <div className="text-xs text-[#9aa39e] mt-1">
                  {a.agent}{a.recipient ? ` · to: ${a.recipient}` : ""}
                </div>
                {a.reason && <div className="text-xs text-[#4A5550] mt-0.5">Why: {a.reason}</div>}
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${a.status === "approved" ? "bg-emerald-100 text-emerald-700" : a.status === "rejected" ? "bg-gray-200 text-gray-600" : "bg-[#62B848]/15 text-[#224733]"}`}>{a.status}</span>
            </div>
            <div className="whitespace-pre-wrap text-sm text-[#1A2421] bg-[#F8F9FA] rounded-xl p-4">{a.body}</div>
            {a.status === "pending" ? (
              <div className="flex flex-wrap gap-2 mt-3">
                <button data-testid={`approve-${a.id}`} onClick={() => approve(a.id)} className="inline-flex items-center gap-1.5 bg-[#62B848] text-white font-bold text-sm px-4 py-2 rounded-full"><Send size={15} /> Approve &amp; send</button>
                <button data-testid={`edit-${a.id}`} onClick={() => edit(a)} className="inline-flex items-center gap-1.5 bg-[#F1F5F0] text-[#224733] font-bold text-sm px-4 py-2 rounded-full"><Pencil size={15} /> Edit then send</button>
                <button data-testid={`reject-${a.id}`} onClick={() => reject(a.id)} className="inline-flex items-center gap-1.5 bg-[#F1F5F0] text-red-600 font-bold text-sm px-4 py-2 rounded-full"><X size={15} /> Reject</button>
              </div>
            ) : a.status === "approved" ? (
              <div className="mt-3 text-xs font-semibold text-[#224733] flex items-center gap-1.5">
                {a.dispatched ? <Check size={14} className="text-emerald-600" /> : <Clock size={14} className="text-amber-500" />}
                {a.dispatch_note || "Approved."}{a.approved_by ? ` · by ${a.approved_by}` : ""}
              </div>
            ) : (
              <div className="mt-3 text-xs text-[#9aa39e]">Rejected{a.reject_reason ? ` — “${a.reject_reason}”` : ""}. Nothing was sent.</div>
            )}
          </Card>
        ))}
        {items.length === 0 && <Card><p className="text-[#4A5550] text-sm">Nothing awaiting approval. Ask an agent to draft something in Command Chat.</p></Card>}
      </div>

      <div>
        <button data-testid="approvals-audit-toggle" onClick={() => setShowLog((v) => !v)} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#224733]">
          <Clock size={15} /> Activity log ({log.length}) <ChevronDown size={16} className={`transition-transform ${showLog ? "rotate-180" : ""}`} />
        </button>
        {showLog && (
          <Card className="mt-3 !p-4">
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {log.map((e) => (
                <div key={e.id} className="text-xs text-[#4A5550] flex flex-wrap gap-x-2">
                  <span className="text-[#9aa39e] w-40 shrink-0">{new Date(e.at).toLocaleString("en-AU")}</span>
                  <span className={`font-bold ${e.action === "send_blocked" || e.action === "dispatch_failed" ? "text-red-600" : e.action === "dispatched" ? "text-emerald-600" : "text-[#224733]"}`}>{e.action}</span>
                  <span>{e.actor}</span>
                  {e.detail && <span className="text-[#9aa39e]">— {e.detail}</span>}
                </div>
              ))}
              {log.length === 0 && <p className="text-sm text-[#4A5550]">No activity yet.</p>}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

const LIVE_NOW = [
  "Public website, 3D marketing & Our Work gallery",
  "Lead capture → New Enquiries (nothing missed)",
  "AI crew + Command Chat (Emergent key)",
  "Approval gate — nothing sends without you",
  "In-app pile-up & morning-digest alerts",
];

const UNLOCKS = {
  openai: "AI crew, chat assistant & drafting",
  resend: "Enquiry & queue-alert emails to your inbox",
  google_places: "Live Google reviews on the public site",
  meta: "Facebook / Instagram posting (after approval)",
  servicem8: "Auto-create jobs from enquiries",
  xero: "Live finance snapshot",
  twilio: "SMS to leads (after approval)",
};

function LaunchPanel({ keys }) {
  const connected = keys.filter((k) => k.status.startsWith("Connected"));
  return (
    <Card data-testid="launch-panel" className="border-[#62B848]/30 bg-[#62B848]/[0.04]">
      <div className="flex items-center gap-2 mb-1">
        <Rocket size={20} className="text-[#62B848]" />
        <h3 className="font-display font-bold text-lg text-[#1A2421]">Launch readiness</h3>
      </div>
      <p className="text-sm text-[#4A5550] mb-5">
        Your site is <span className="font-bold text-[#224733]">live and safe to launch now</span>. {connected.length} of {keys.length} integrations connected — the rest switch on the instant you paste a key here, <span className="font-semibold">no redeploy needed</span>.
      </p>
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-[#224733] mb-2">Live now</div>
          <ul className="space-y-1.5">
            {LIVE_NOW.map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-[#1A2421]">
                <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-[#224733] mb-2">Integrations</div>
          <ul className="space-y-1.5">
            {keys.map((k) => {
              const on = k.status.startsWith("Connected");
              return (
                <li key={k.key} data-testid={`launch-key-${k.key}`} className="flex items-start gap-2 text-sm">
                  {on ? <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /> : <Clock size={16} className="text-amber-500 mt-0.5 shrink-0" />}
                  <span className={on ? "text-[#1A2421]" : "text-[#4A5550]"}>
                    <span className="font-semibold">{k.label}</span> — {UNLOCKS[k.key] || k.note}
                    {!on && <span className="text-amber-600 font-semibold"> · add key below</span>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </Card>
  );
}

function TeamAccess() {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { ok: bool, text }
  const load = useCallback(() => api.get("/settings/users").then((r) => setUsers(r.data.users)).catch((e) => console.error("Failed to load team", e)), []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim() || busy) return;
    setBusy(true); setMsg(null);
    try {
      await api.post("/settings/users", { email: email.trim() });
      setEmail("");
      await load();
      setMsg({ ok: true, text: "Access granted — they can now sign in with their Google account." });
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Couldn't add that email." });
    }
    setBusy(false);
    setTimeout(() => setMsg(null), 6000);
  };

  const remove = async (em) => {
    if (!window.confirm(`Remove ${em}? They'll lose access to the command centre immediately.`)) return;
    try { await api.delete(`/settings/users?email=${encodeURIComponent(em)}`); await load(); }
    catch (e) { console.error("Failed to remove team member", e); }
  };

  return (
    <Card data-testid="team-access-card">
      <div className="flex items-center gap-2 mb-1">
        <Users size={20} className="text-[#62B848]" />
        <h3 className="font-display font-bold text-lg text-[#1A2421]">Team access</h3>
      </div>
      <p className="text-sm text-[#4A5550] mb-5">
        Invite people to the command centre by email. They sign in with their own Google account and get access to everything <span className="font-semibold">except Settings, API keys and team management</span> — those stay owner-only.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input data-testid="team-invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") invite(); }}
          placeholder="name@email.com"
          className="flex-1 rounded-xl border border-black/10 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B848]" />
        <button data-testid="team-invite-btn" onClick={invite} disabled={busy}
          className="inline-flex items-center justify-center gap-2 bg-[#62B848] text-white font-bold text-sm px-5 py-2.5 rounded-full disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />} Invite
        </button>
      </div>
      {msg && <p data-testid="team-msg" className={`text-sm font-semibold mb-4 ${msg.ok ? "text-[#224733]" : "text-red-600"}`}>{msg.text}</p>}

      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.email} data-testid={`team-user-${u.email}`} className="flex items-center justify-between gap-3 border border-black/5 rounded-xl px-4 py-3">
            <div className="min-w-0">
              <div className="font-semibold text-sm text-[#1A2421] truncate">{u.email}</div>
              <div className="text-xs text-[#9aa39e]">{u.role === "owner" ? "Owner · full access" : "Team member · limited access"}</div>
            </div>
            {u.removable
              ? <button data-testid={`team-remove-${u.email}`} onClick={() => remove(u.email)} className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full"><Trash2 size={14} /> Remove</button>
              : <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-[#224733] text-white shrink-0">OWNER</span>}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function SettingsScreen({ onLogout }) {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const load = useCallback(() => api.get("/settings").then((r) => setS(r.data)).catch((e) => console.error("Failed to load settings", e)), []);
  useEffect(() => { load(); }, [load]);
  if (!s) return <Card><Loader2 className="animate-spin mx-auto text-[#62B848]" /></Card>;
  const setB = (k) => (e) => setS((p) => ({ ...p, business: { ...p.business, [k]: e.target.value } }));
  const saveB = async () => { await api.put("/settings/business", s.business); setMsg("Business details published to the live site."); setTimeout(() => setMsg(""), 3000); };
  const saveKey = async (key, value) => { await api.put("/settings/keys", { key, value }); await load(); };
  const testEmail = async () => {
    setEmailMsg("Sending test email…");
    try { const r = await api.post("/settings/test-email"); setEmailMsg(`Test email sent to ${r.data.to} ✓`); }
    catch (e) { setEmailMsg(e?.response?.data?.detail || "Test failed — check the key."); }
    setTimeout(() => setEmailMsg(""), 6000);
  };
  const toggleAgent = async (key) => { const agents = {}; s.agents.forEach((a) => agents[a.key] = { enabled: a.key === key ? !a.enabled : a.enabled }); await api.put("/settings/agents", { agents }); await load(); };
  const toggleNotif = async (k) => { const n = { ...s.notifications, [k]: !s.notifications[k] }; await api.put("/settings/notifications", { notifications: n }); setS((p) => ({ ...p, notifications: n })); };
  const cls = "w-full rounded-xl border border-black/10 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B848]";

  return (
    <div className="space-y-6 max-w-3xl">
      <H>Settings</H>

      <LaunchPanel keys={s.keys} />

      <Card>
        <h3 className="font-display font-bold text-lg mb-4">Business details</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <input className={cls} value={s.business.tagline} onChange={setB("tagline")} placeholder="Tagline" />
          <input className={cls} value={s.business.phone} onChange={setB("phone")} placeholder="Phone" />
          <input className={cls} value={s.business.email} onChange={setB("email")} placeholder="Email" />
          <input type="number" className={cls} value={s.business.service_area_radius_km} onChange={setB("service_area_radius_km")} placeholder="Radius km" />
          <input className={`${cls} sm:col-span-2`} value={s.business.address} onChange={setB("address")} placeholder="Address" />
          <div className="sm:col-span-2">
            <label className="text-xs font-bold uppercase tracking-wide text-[#4A5550] mb-1.5 block">ServiceM8 booking URL</label>
            <input data-testid="settings-booking-url" className={cls} value={s.business.booking_url || ""} onChange={setB("booking_url")} placeholder="https://book.servicem8.com/request_booking?uuid=…" />
            <p className="text-xs text-[#9aa39e] mt-1">Paste your ServiceM8 booking link. When set, the site's “Get a free quote” buttons open it in an on-brand pop-up. Leave blank to use the built-in enquiry form.</p>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4"><button data-testid="settings-save-business" onClick={saveB} className="bg-[#62B848] text-white font-bold text-sm px-5 py-2.5 rounded-full">Save &amp; publish</button>{msg && <span className="text-sm font-semibold text-[#224733]">{msg}</span>}</div>
      </Card>

      <Card>
        <h3 className="font-display font-bold text-lg mb-4">API keys &amp; connections</h3>
        <div className="space-y-2">{s.keys.map((k) => (
          <div key={k.key} className="flex flex-wrap items-center gap-3 border border-black/5 rounded-xl px-4 py-3">
            <div className="min-w-[140px]"><div className="font-bold text-[#1A2421] text-sm">{k.label}</div><div className="text-xs text-[#9aa39e]">{k.note}</div></div>
            <span className={`text-xs font-bold px-2 py-1 rounded-full ${k.status.startsWith("Connected") ? "bg-emerald-100 text-emerald-700" : k.status === "Pending key" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{k.status}</span>
            <input id={`key-${k.key}`} className="flex-1 min-w-[160px] rounded-lg border border-black/10 px-3 py-1.5 text-sm" placeholder={k.masked || "Paste key…"} />
            <button data-testid={`key-save-${k.key}`} onClick={() => { const v = document.getElementById(`key-${k.key}`).value; if (v) saveKey(k.key, v); }} className="bg-[#224733] text-white text-xs font-bold px-3 py-1.5 rounded-full">Save</button>
            {k.key === "resend" && <button data-testid="key-test-resend" onClick={testEmail} className="bg-[#62B848] text-white text-xs font-bold px-3 py-1.5 rounded-full">Test email</button>}
          </div>
        ))}</div>
        {emailMsg && <p className="text-sm font-semibold text-[#224733] mt-3">{emailMsg}</p>}
      </Card>

      <Card>
        <h3 className="font-display font-bold text-lg mb-4">Agents</h3>
        <div className="grid sm:grid-cols-2 gap-3">{s.agents.map((a) => (
          <div key={a.key} className="border border-black/5 rounded-xl px-4 py-3 flex items-center justify-between">
            <div><div className="font-bold text-sm text-[#1A2421]">{a.label}</div><div className="text-xs text-[#9aa39e]">{a.autonomy}</div></div>
            <button onClick={() => toggleAgent(a.key)} className={`w-11 h-6 rounded-full transition-colors relative ${a.enabled ? "bg-[#62B848]" : "bg-gray-300"}`}><span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${a.enabled ? "left-[22px]" : "left-0.5"}`} /></button>
          </div>
        ))}</div>
      </Card>

      <Card>
        <h3 className="font-display font-bold text-lg mb-4">Notifications</h3>
        {Object.entries({ new_lead: "New lead", awaiting_approval: "Item awaiting approval", negative_review: "Negative review", daily_brief: "Daily brief ready" }).map(([k, label]) => (
          <label key={k} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0"><span className="text-sm text-[#1A2421]">{label}</span>
            <input type="checkbox" checked={!!s.notifications[k]} onChange={() => toggleNotif(k)} className="w-5 h-5 accent-[#62B848]" /></label>
        ))}
      </Card>

      <TeamAccess />

      <Card>
        <h3 className="font-display font-bold text-lg mb-3">Account</h3>
        <p className="text-sm text-[#4A5550] mb-4">Signed in as <span className="font-semibold text-[#1A2421]">{s.account.name} ({s.account.email})</span> · Google login</p>
        <button onClick={onLogout} className="bg-[#F1F5F0] text-red-600 font-bold text-sm px-5 py-2.5 rounded-full">Sign out</button>
      </Card>
    </div>
  );
}
